'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';

interface MagazineIssue {
  id: string;
  coverImage: string;
  issueNumber: string;
  publicationDate: string;
  title: string;
  description: string;
  category: string;
  featured: boolean;
}

interface FeaturedArticle {
  id: string;
  title: string;
  description: string;
  issueNumber: string;
  coverImage: string;
}

export default function MagazineInteractive() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Mock data for magazine issues
  const magazineIssues: MagazineIssue[] = [
  {
    id: '1',
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18683733a-1765503245685.png",
    issueNumber: 'Issue 45',
    publicationDate: 'November 2025',
    title: 'Construction Technology Revolution',
    description: 'Latest innovations in construction machinery and digital transformation in the industry',
    category: 'monthly',
    featured: true
  },
  {
    id: '2',
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f5f9485e-1766290057760.png",
    issueNumber: 'Issue 44',
    publicationDate: 'October 2025',
    title: 'Heavy Equipment Maintenance Guide',
    description: 'Comprehensive maintenance strategies for heavy construction equipment',
    category: 'monthly',
    featured: false
  },
  {
    id: '3',
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_14614e01d-1765995473561.png",
    issueNumber: 'Special Report 2025',
    publicationDate: 'September 2025',
    title: 'Industry Outlook 2026',
    description: 'Special report on construction industry trends and predictions',
    category: 'special',
    featured: true
  },
  {
    id: '4',
    coverImage: "https://images.unsplash.com/photo-1689439518549-7ff283abe884",
    issueNumber: 'Issue 43',
    publicationDate: 'August 2025',
    title: 'Safety First: Best Practices',
    description: 'Comprehensive safety guidelines and best practices for construction sites',
    category: 'monthly',
    featured: false
  }];


  // Mock data for featured articles
  const featuredArticles: FeaturedArticle[] = [
  {
    id: '1',
    title: 'AI-Powered Construction: The Future is Now',
    description: 'How artificial intelligence is transforming construction project management',
    issueNumber: 'Issue 45',
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_14f2a2afe-1767199715201.png"
  },
  {
    id: '2',
    title: 'Sustainable Equipment: Going Green',
    description: 'Electric and hybrid machinery leading the sustainability revolution',
    issueNumber: 'Issue 44',
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff63806f-1767718945074.png"
  }];


  const categories = [
  { value: 'all', label: 'All Issues' },
  { value: 'monthly', label: 'Monthly Magazine' },
  { value: 'special', label: 'Special Reports' },
  { value: 'technical', label: 'Technical Guides' }];


  const filteredIssues = magazineIssues?.filter((issue) => {
    const matchesCategory = selectedCategory === 'all' || issue?.category === selectedCategory;
    const matchesSearch =
    issue?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    issue?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    issue?.issueNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredIssues = magazineIssues?.filter((issue) => issue?.featured);

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-2xl p-8 md:p-12">
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Industry Magazine</h1>
          <p className="text-xl mb-6 text-amber-50">
            Access comprehensive industry publications with digital reading experience, archived issues, and featured articles
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="bg-white text-amber-600 px-6 py-3 rounded-lg font-semibold hover:bg-amber-50 transition-colors">
              Subscribe Now
            </button>
            <button className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors">
              Browse Archive
            </button>
          </div>
        </div>
      </div>

      {/* Featured Issues Carousel */}
      {featuredIssues?.length > 0 &&
      <section>
          <h2 className="text-3xl font-bold mb-6">Featured Issues</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredIssues?.map((issue) =>
          <div
            key={issue?.id}
            className="relative group cursor-pointer rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">

                <div className="aspect-[3/4] relative">
                  <AppImage
                src={issue?.coverImage}
                alt={`${issue?.title} - ${issue?.issueNumber} cover`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <span className="inline-block bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-2">
                      Featured
                    </span>
                    <h3 className="text-2xl font-bold mb-2">{issue?.title}</h3>
                    <p className="text-sm text-gray-200 mb-2">{issue?.issueNumber} • {issue?.publicationDate}</p>
                    <p className="text-sm text-gray-300">{issue?.description}</p>
                  </div>
                </div>
              </div>
          )}
          </div>
        </section>
      }

      {/* Search and Filter Section */}
      <section>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by title, issue number, or topic..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-600" />

          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories?.map((category) =>
            <button
              key={category?.value}
              onClick={() => setSelectedCategory(category?.value)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              selectedCategory === category?.value ?
              'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`
              }>

                {category?.label}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Magazine Archive Grid */}
      <section>
        <h2 className="text-3xl font-bold mb-6">Magazine Archive</h2>
        {filteredIssues?.length > 0 ?
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredIssues?.map((issue) =>
          <div
            key={issue?.id}
            className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group">

                <div className="aspect-[3/4] relative overflow-hidden">
                  <AppImage
                src={issue?.coverImage}
                alt={`${issue?.title} - ${issue?.issueNumber} cover`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />

                </div>
                <div className="p-4">
                  <span className="inline-block bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs font-semibold mb-2">
                    {issue?.issueNumber}
                  </span>
                  <h3 className="text-lg font-bold mb-1 line-clamp-2">{issue?.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{issue?.publicationDate}</p>
                  <p className="text-sm text-gray-700 line-clamp-2 mb-3">{issue?.description}</p>
                  <button className="w-full bg-amber-600 text-white py-2 rounded-lg font-semibold hover:bg-amber-700 transition-colors">
                    Read Now
                  </button>
                </div>
              </div>
          )}
          </div> :

        <div className="text-center py-12">
            <svg
            className="w-16 h-16 mx-auto text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">

              <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />

            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No issues found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        }
      </section>

      {/* Featured Articles Section */}
      <section className="bg-gray-50 rounded-2xl p-8">
        <h2 className="text-3xl font-bold mb-6">Featured Articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featuredArticles?.map((article) =>
          <div
            key={article?.id}
            className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all cursor-pointer group">

              <div className="aspect-video relative overflow-hidden">
                <AppImage
                src={article?.coverImage}
                alt={`${article?.title} featured article`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

              </div>
              <div className="p-6">
                <span className="inline-block text-amber-600 text-sm font-semibold mb-2">
                  {article?.issueNumber}
                </span>
                <h3 className="text-xl font-bold mb-2 group-hover:text-amber-600 transition-colors">
                  {article?.title}
                </h3>
                <p className="text-gray-700 mb-4">{article?.description}</p>
                <button className="text-amber-600 font-semibold hover:text-amber-700 transition-colors flex items-center gap-2">
                  Read Full Article
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Subscription CTA */}
      <section className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-2xl p-8 md:p-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Never Miss an Issue</h2>
          <p className="text-xl text-gray-300 mb-8">
            Subscribe to get instant access to all magazine issues, exclusive content, and premium features
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <input
              type="email"
              placeholder="Enter your email address"
              className="px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-600 flex-1 max-w-md" />

            <button className="bg-amber-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-amber-700 transition-colors">
              Subscribe Free
            </button>
          </div>
          <p className="text-sm text-gray-400 mt-4">
            Free for all registered members • Access to complete archive • Offline reading
          </p>
        </div>
      </section>
    </div>);

}